import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Alert, Form } from '@themesberg/react-bootstrap';
import { END_POINT_ROLES } from '../const';
import { showSuccessToast } from '../utils/toast';
import EditFormLayout from '../components/EditFormLayout';
import RecordEditFeedback from '../components/RecordEditFeedback';
import RecordEditActions from '../components/RecordEditActions';
import UserPermissionFields from '../components/UserPermissionFields';
import useRecordEditor from '../hooks/useRecordEditor';

export const RoleEditForm = ({ roleId, onSuccess, onCancel, onSubmittingChange }) => {
  const editor = useRecordEditor({
    recordId: roleId, endpoint: END_POINT_ROLES, label: 'role',
    onSuccess, onSubmittingChange,
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editor.record?.name || '',
      permissions: editor.record?.permissions && !Array.isArray(editor.record.permissions)
        ? editor.record.permissions : {},
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required('Role name is required'),
    }),
    onSubmit: editor.submit,
  });

  if (editor.loading || editor.loadError || !editor.record) {
    return <RecordEditFeedback loading={editor.loading} error={editor.loadError}
      label="role" onRetry={editor.retry} onCancel={onCancel} />;
  }

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting}>
      {formik.status && <Alert variant="danger">{formik.status}</Alert>}
      <Form.Group className="mb-3" controlId="edit-role-name">
        <Form.Label>Role Name <span className="text-danger">*</span></Form.Label>
        <Form.Control type="text" autoFocus disabled={formik.isSubmitting}
          {...formik.getFieldProps('name')}
          isInvalid={Boolean(formik.touched.name && formik.errors.name)} />
        <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
      </Form.Group>
      <UserPermissionFields formik={formik} title="Role Permissions" />
      <RecordEditActions formik={formik} backTo="/roles" onCancel={onCancel} />
    </Form>
  );
};

export const EditRoleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const handleSuccess = result => {
    showSuccessToast(result.message || 'Role updated successfully.');
    navigate('/roles');
  };
  return (
    <EditFormLayout title="Edit Role" subtitle="Update role details" backTo="/roles">
      <RoleEditForm key={id} roleId={id} onSuccess={handleSuccess} />
    </EditFormLayout>
  );
};
